import {
  BriefcaseBusiness,
  CalendarOff,
  Clock3,
  MapPin,
  ShieldBan,
  UsersRound,
  X,
} from "lucide-react";

import { businessDateKey, formatDate, formatTime } from "./schedule-date";
import type { CalendarEvent, CalendarTeam } from "./types";

export function DayView({ date, events, onOpen }: ViewProps & { date: string }) {
  const items = eventsFor(date, events);
  return (
    <div className="schedule-day-view">
      <div className="day-date-block">
        <strong>{formatDate(date, { day: "2-digit" })}</strong>
        <span>{formatDate(date, { weekday: "long", month: "long" })}</span>
      </div>
      <EventStack events={items} onOpen={onOpen} empty="No work or blocked time on this day." />
    </div>
  );
}

export function WeekView({ dates, events, onOpen }: ViewProps & { dates: string[] }) {
  return (
    <div className="schedule-week-grid">
      {dates.map((date) => (
        <section className="schedule-week-day" key={date}>
          <header>
            <span>{formatDate(date, { weekday: "short" })}</span>
            <strong>{formatDate(date, { day: "numeric" })}</strong>
          </header>
          <EventStack events={eventsFor(date, events)} onOpen={onOpen} compact />
        </section>
      ))}
    </div>
  );
}

export function MonthView({
  dates,
  anchor,
  events,
  onOpen,
}: ViewProps & { dates: string[]; anchor: string }) {
  return (
    <div className="schedule-month-grid">
      {dates.slice(0, 7).map((date) => (
        <span className="month-weekday" key={`weekday-${date}`}>
          {formatDate(date, { weekday: "short" })}
        </span>
      ))}
      {dates.map((date) => {
        const items = eventsFor(date, events);
        return (
          <section
            className={`schedule-month-day ${date.slice(0, 7) !== anchor.slice(0, 7) ? "outside" : ""}`}
            key={date}
          >
            <strong>{formatDate(date, { day: "numeric" })}</strong>
            {items.slice(0, 3).map((event) => (
              <EventPill key={`${event.kind}-${event.id}`} event={event} onOpen={onOpen} tiny />
            ))}
            {items.length > 3 ? (
              <span className="more-events">+{items.length - 3} more</span>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

export function TeamView({
  date,
  events,
  teams,
  selectedTeamId,
  onOpen,
}: ViewProps & { date: string; teams: CalendarTeam[]; selectedTeamId: string }) {
  const team = teams.find((item) => item.id === selectedTeamId);
  const items = eventsFor(date, events);
  return (
    <div className="team-schedule-view">
      <header className="team-lane-heading">
        <span className="team-lane-icon">
          <UsersRound />
        </span>
        <div>
          <span className="eyebrow">Team lane</span>
          <h3>{team?.name ?? "Choose a team"}</h3>
        </div>
        <span className={`team-state ${team?.active ? "active" : ""}`}>
          {team ? (team.active ? "Active" : "Inactive") : "No team"}
        </span>
      </header>
      <div className="team-timeline">
        <span className="timeline-start">8 AM</span>
        <span className="timeline-end">6 PM</span>
        <EventStack events={items} onOpen={onOpen} empty="No work or blocked time for this team." />
      </div>
    </div>
  );
}

function EventStack({
  events,
  onOpen,
  empty,
  compact = false,
}: {
  events: CalendarEvent[];
  onOpen: (event: CalendarEvent) => void;
  empty?: string;
  compact?: boolean;
}) {
  if (!events.length) return empty ? <div className="calendar-empty">{empty}</div> : null;
  return (
    <div className={`event-stack ${compact ? "compact" : ""}`}>
      {events.map((event) => (
        <EventPill key={`${event.kind}-${event.id}`} event={event} onOpen={onOpen} />
      ))}
    </div>
  );
}

function EventPill({
  event,
  onOpen,
  tiny = false,
}: {
  event: CalendarEvent;
  onOpen: (event: CalendarEvent) => void;
  tiny?: boolean;
}) {
  const Icon =
    event.kind === "APPOINTMENT"
      ? BriefcaseBusiness
      : event.kind === "TIME_OFF"
        ? CalendarOff
        : ShieldBan;
  return (
    <button
      className={`schedule-event ${event.kind.toLowerCase()} ${tiny ? "tiny" : ""}`}
      onClick={() => onOpen(event)}
      title={event.title}
    >
      <Icon />
      <span>
        <strong>
          {formatTime(event.startAt)} · {event.title}
        </strong>
        {tiny ? null : (
          <small>
            {event.serviceName ?? event.employeeName ?? event.teamName ?? "Protected time"}
          </small>
        )}
      </span>
    </button>
  );
}

export function EventDetail({ event, onClose }: { event: CalendarEvent; onClose: () => void }) {
  const kind =
    event.kind === "APPOINTMENT"
      ? "Customer appointment"
      : event.kind === "TIME_OFF"
        ? "Time off"
        : "Schedule block";
  return (
    <div
      className="client-drawer-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside
        className="client-drawer schedule-detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-detail-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="drawer-header">
          <div>
            <span className="eyebrow lime">{kind}</span>
            <h2 id="schedule-detail-title">{event.title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close appointment detail">
            <X />
          </button>
        </header>
        <div className="schedule-detail-body">
          <Detail
            icon={Clock3}
            label="Time"
            value={`${formatDate(businessDateKey(event.startAt), { weekday: "long", month: "long", day: "numeric" })}, ${formatTime(event.startAt)} – ${formatTime(event.endAt)}`}
          />
          <Detail
            icon={UsersRound}
            label="Team or person"
            value={event.teamName ?? event.employeeName ?? "Unassigned"}
          />
          {event.serviceName ? (
            <Detail icon={BriefcaseBusiness} label="Service" value={event.serviceName} />
          ) : null}
          {event.propertyAddress ? (
            <Detail
              icon={MapPin}
              label="Location"
              value={`${event.propertyAddress}, ${event.propertyCity}, ${event.propertyState}`}
            />
          ) : null}
          <div className="schedule-detail-note">
            <ShieldBan />
            <p>
              Reassignment and rescheduling remain locked until every availability and travel
              conflict is revalidated transactionally.
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Detail({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Clock3;
  label: string;
  value: string;
}) {
  return (
    <div className="schedule-detail-row">
      <Icon />
      <span>
        <small>{label}</small>
        <strong>{value}</strong>
      </span>
    </div>
  );
}

function eventsFor(date: string, events: CalendarEvent[]) {
  return events.filter((event) => {
    const inclusiveEnd = new Date(new Date(event.endAt).getTime() - 1);
    return date >= businessDateKey(event.startAt) && date <= businessDateKey(inclusiveEnd);
  });
}

type ViewProps = { events: CalendarEvent[]; onOpen: (event: CalendarEvent) => void };
